import {Money, ShopifyAnalyticsPayload} from '@shopify/hydrogen';
import {Product, ProductVariant} from '@shopify/hydrogen/storefront-api-types';
import clsx from 'clsx';

import {useFetcher, useLocation, useMatches} from '@remix-run/react';
import {DEFAULT_LOCALE} from '~/lib/utils';
import ProductForm from '~/components/product/Form';
import type {SanityProductPage} from '~/lib/sanity';
import {hasMultipleProductOptions} from '~/lib/utils';

type Props = {
  sanityProduct: SanityProductPage;
  storefrontProduct: Product;
  selectedVariant: ProductVariant;
  analytics: ShopifyAnalyticsPayload;
};

function ProductPrices({
  storefrontProduct,
  selectedVariant,
}: {
  storefrontProduct: Product;
  selectedVariant: ProductVariant;
}) {
  if (!storefrontProduct || !selectedVariant) {
    return null;
  }

  return (
    <div className="mt-2 flex text-lg">
      {selectedVariant.compareAtPrice && (
        <span className="mr-3 text-darkGray line-through decoration-red">
          <Money data={selectedVariant.compareAtPrice} />
          <span className="pl-2">inkl. MwSt.</span>
        </span>
      )}
      {selectedVariant.price && (
        <>
          <Money data={selectedVariant.price} withoutCurrency={false} />
          <span className="pl-2">inkl. MwSt.</span>
        </>
      )}
    </div>
  );
}

export default function ProductWidget({
  sanityProduct,
  storefrontProduct,
  selectedVariant,
  analytics,
}: Props) {
  const availableForSale = selectedVariant?.availableForSale;

  if (!selectedVariant) {
    return null;
  }

  const [root] = useMatches();
  const selectedLocale = root.data?.selectedLocale ?? DEFAULT_LOCALE;
  const lang = selectedLocale.language;
  let skuDisp = 'Artikelnummer: ';
  if (lang === 'EN') {
    skuDisp = 'Stylecode: ';
  }

  let SKU = storefrontProduct.variants?.nodes?.[0].sku;
  let displaySKU = '';
  if (SKU !== null && SKU !== undefined) {
    displaySKU = SKU.slice(0, -4);
  }

  return (
    <div className={clsx('pointer-events-auto z-10 ml-auto h-full', '')}>
      {/* Sold out */}

      {/*
 {!availableForSale && (
        <div className="mb-3 text-lg font-bold uppercase text-red">
          Sold out
        </div>
      )}
        */}

      {/* Sale */}
      {availableForSale && selectedVariant?.compareAtPrice && (
        <div className="mb-3 text-lg font-bold uppercase text-red">Sale</div>
      )}

      {/* Title */}
      {storefrontProduct?.title && (
        <>
          <h1 className=" mb-2 mb-2 text-xl font-bold">
            {storefrontProduct.title}
          </h1>
          <span className="pt-1">
            {skuDisp} {displaySKU}
          </span>
        </>
      )}

      {/* Vendor */}
      {storefrontProduct?.vendor && (
        <div className="mt-1 text-md text-darkGray">
          {storefrontProduct.vendor}
        </div>
      )}

      {/* Prices */}
      <ProductPrices
        storefrontProduct={storefrontProduct}
        selectedVariant={selectedVariant}
      />

      {/* Divider */}
      <div className="my-4 w-full border-b border-gray" />

      {/* Product options */}
      <ProductForm
        product={storefrontProduct}
        selectedVariant={selectedVariant}
        analytics={analytics}
        customProductOptions={sanityProduct.customProductOptions}
        lang={lang}
      />
    </div>
  );
}
